/**
 * Pure helpers for the panelist persona and the panel card. Shared by the
 * middleware, the root layout and PanelCard, and unit tested on Node's runner,
 * so no path aliases or React here.
 *
 * Plan 09 C09-19 (D09-11, D09-12): the My panels pages are gone. Panelists act
 * by replying to the panel invite email, so a panelist-only session lands on
 * the static "Panel reviews happen by email" page instead of a 404 or a loop.
 *
 * The role rule mirrors app/core/roles.is_panelist_only on the backend. It is
 * presentation only: the real gate is app/authz.map.json (Rules 12 and 13).
 */

/** Where a panelist-only session lives, and where the old /panel routes point (D09-12). */
export const PANELIST_HOME = '/panel-email'

/** Any of these keeps the full app shell, so the panelist redirect never applies (D9). */
export const SHELL_ROLES: readonly string[] = ['admin', 'user', 'logistics_leader']

export function isPanelistOnly(roles: readonly string[] | null | undefined): boolean {
  const held = roles ?? []
  return held.includes('panelist') && !held.some((r) => SHELL_ROLES.includes(r))
}

/** Paths a panelist-only session may open; everything else redirects to PANELIST_HOME. */
export function panelistMayOpen(pathname: string): boolean {
  return (
    pathname === PANELIST_HOME ||
    pathname.startsWith(`${PANELIST_HOME}/`) ||
    pathname.startsWith('/auth') ||
    pathname.startsWith('/api/auth')
  )
}

/**
 * The middleware's decision for a signed-in session: the path to redirect to,
 * or null to let the request through. Only a panelist-only session is moved,
 * and never off a page it may open, so there is no redirect loop.
 */
export function panelistRedirect(roles: readonly string[] | null | undefined, pathname: string): string | null {
  return isPanelistOnly(roles) && !panelistMayOpen(pathname) ? PANELIST_HOME : null
}

/** A meeting link from the backend is rendered only when it is a well-formed https URL. */
export function safeMeetingUrl(url: string | null | undefined): string | null {
  if (!url) return null
  try {
    const parsed = new URL(url)
    return parsed.protocol === 'https:' ? parsed.toString() : null
  } catch {
    return null
  }
}
